import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Button,
  Icon,
  Text,
  Grid,
  GridItem,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Avatar,
  VStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  chakra,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Link as ChakraLink,
  IconButton,
  Center,
  Spinner,
  HStack,
} from '@chakra-ui/react'
import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@chakra-ui/icons'
import _ from 'lodash'
import { HiOutlineUserGroup, HiUserGroup } from 'react-icons/hi'
import { RiFolder3Line, RiFile2Line } from 'react-icons/ri'
import { MdCollections } from 'react-icons/md'
import { components } from 'react-select'
import { Link } from 'react-router-dom'
import { Base64 } from 'js-base64'
import { IKReactSelect, colors } from '../../../../../ui-kit'
import type {
  ShareModalGroupedOptions,
  ShareModalMediaCollection,
  ShareModalOptions,
  ShareModalPermissionOption,
  ShareModalState,
  UpdateAccessListRequest,
  ViewableParentSuccessResponse,
} from '../../../../interfaces'
import type {
  CreatedBy,
  InheritenceListItem,
  User,
  UserGroup,
} from '../../../../interfaces/MediaLibrarySearchResponse'
import { ACLEntityTypeEnum, ACLPermissionEnum } from '../../../../constants'
import { MLContext, UserContext } from '../../../../providers'
import InheritedPermissionIcon from '../../../../../graphics/media-library/InheritedPermissionIcon'
import {
  aclPermissionOrderMapping,
  PermissionDescriptionMap,
} from '../../../../../utils/permissions'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import { isRestrictedUser } from '../../../../../utils/roles'

interface Props {
  assetName: string
  assetType: string
  isOpen: boolean
  loadingAccessList: boolean
  onClose(): void
  getAccessList(): Promise<ShareModalState | undefined>
  updateAccessList(requestBody: UpdateAccessListRequest): Promise<void>
  getViewableParent?(): Promise<ViewableParentSuccessResponse>
}

const getHighestInheritedPermisssion = (permissionArr: string[]): string => {
  let highestPermission = permissionArr[0]
  permissionArr.forEach(permission => {
    if (
      aclPermissionOrderMapping[permission] >
      aclPermissionOrderMapping[highestPermission]
    ) {
      highestPermission = permission
    }
  })
  return highestPermission
}

const ShareModal = ({
  assetName,
  assetType,
  isOpen = false,
  loadingAccessList,
  onClose,
  getAccessList,
  updateAccessList,
  getViewableParent,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [{ refetch, refetchViewableParent, refetchFolderPermission }, dispatch] = useContext(MLContext)

  // list of users and groups already in asset/media-collection ACL
  const [accessList, setAccessList] = useState<ShareModalOptions[]>([])
  const [initialState, setInitialState] = useState<ShareModalState>({
    accessList: [],
    users: [],
    userGroups: [],
    mediaCollections: [],
  })
  // these are the options available to IKReactSelectCreatable
  const [userOptions, setUserOptions] = useState<ShareModalOptions[]>([])
  const [userGroupOptions, setUserGroupOptions] = useState<ShareModalOptions[]>(
    [],
  )
  const [groupedOptions, setGroupedOptions] = useState<
    ShareModalGroupedOptions[]
  >([])
  const [selectedOptions, setSelectedOptions] = useState<ShareModalOptions[]>(
    [],
  )
  const [mediaCollectionList, setMediaCollectionList] = useState<
    ShareModalOptions[]
  >([])
  const [selectedPermission, setSelectedPermission] = useState<string>(
    ACLPermissionEnum.READ,
  )
  const [createdBy, setCreatedBy] = useState<CreatedBy>()
  const [isMediaCollectionTabSelected, setIsMediaCollectionTabSelected] =
    useState<boolean>(false)

  const createPermissionOptions = ({
    label,
    description,
    value,
    isDisabled = false,
  }: {
    label: string
    value: string
    description: string
    isDisabled?: boolean
  }): ShareModalPermissionOption => ({
    label: (
      <Flex direction="column">
        <Text>{label}</Text>
        <Text>{description}</Text>
      </Flex>
    ),
    value,
    isDisabled,
  })

  const MultiValueLabel = (props): JSX.Element => {
    const { data } = props
    if (data.value.entity.type === ACLEntityTypeEnum.USER) {
      return (
        <components.MultiValueLabel {...props}>
          <Flex as="span" alignItems="center" maxW="150px">
            <Avatar size="xs" name={data.extraInfo.userName} />
            <Text as="span" fontSize="sm" isTruncated ml="0.75em">
              {data.extraInfo.userName}
            </Text>
          </Flex>
        </components.MultiValueLabel>
      )
    }
    return (
      <components.MultiValueLabel {...props}>
        <Flex alignItems="center" maxW="150px">
          <Icon
            as={HiUserGroup}
            backgroundColor={colors.appGreyTwo}
            borderRadius="full"
            h={6}
            w={6}
          />
          <Text as="span" fontSize="sm" isTruncated ml="0.75em">
            {data.extraInfo.label}
          </Text>
        </Flex>
      </components.MultiValueLabel>
    )
  }

  const filterOptions = (candidate, input): boolean => {
    if (input) {
      if (candidate.data.value.entity.type === ACLEntityTypeEnum.USER) {
        return (candidate.data.extraInfo as User).userName
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      return (candidate.data.extraInfo as UserGroup).label
        .toLowerCase()
        .includes(input.toLowerCase())
    }
    return true
  }

  const getAndSetAccessList = async (): Promise<void> => {
    const data = await getAccessList()
    if (data) {
      const {
        accessList: existingAccessList,
        users: availableUsers,
        userGroups: availableGroups,
        createdBy: assetOwner,
        mediaCollections: existingMediaCollections,
      } = data
      setAccessList(existingAccessList)
      setUserOptions(availableUsers)
      setUserGroupOptions(availableGroups)
      setMediaCollectionList(existingMediaCollections ?? [])
      setGroupedOptions([
        {
          label: t('restricted_users', 'Restricted Users'),
          options: availableUsers,
        },
        {
          label: t('groups', 'Groups'),
          options: availableGroups,
        },
      ])
      setSelectedOptions([])
      setSelectedPermission(ACLPermissionEnum.READ)
      setInitialState({
        accessList: _.cloneDeep(existingAccessList),
        users: _.cloneDeep(availableUsers),
        userGroups: _.cloneDeep(availableGroups),
        mediaCollections: [],
      })
      setCreatedBy(assetOwner)
    }
  }

  // each time modal opens
  useEffect(() => {
    if (isOpen) {
      void getAndSetAccessList()
    }
  }, [isOpen])

  // each time options change
  useEffect(() => {
    setGroupedOptions([
      {
        label: 'Users',
        options: userOptions,
      },
      {
        label: 'Groups',
        options: userGroupOptions,
      },
    ])
  }, [userOptions, userGroupOptions])

  const onSubmit = async (): Promise<void> => {
    // prepare post request body
    const requestBody: UpdateAccessListRequest = {
      acl: {
        addOrModify: [],
        remove: [],
      },
    }
    // acl entities to be removed
    requestBody.acl.remove = initialState.accessList
      .filter(
        intialAccessListItem =>
          !accessList.find(currentAccessListItem =>
            _.isEqual(
              intialAccessListItem.value.entity,
              currentAccessListItem.value.entity,
            ),
          ) &&
          !selectedOptions.find(selectedOption =>
            _.isEqual(
              intialAccessListItem.value.entity,
              selectedOption.value.entity,
            ),
          ),
      )
      .map(augmentedOption => ({
        entity: augmentedOption.value.entity,
      }))
    // acl entities to be added
    requestBody.acl.addOrModify = selectedOptions.map(augmentedOption => ({
      entity: augmentedOption.value.entity,
      permission: ACLPermissionEnum[selectedPermission],
    }))
    // acl entities to be modified
    requestBody.acl.addOrModify = [
      ...requestBody.acl.addOrModify,
      ...accessList
        .filter(modifiedItem =>
          initialState.accessList.find(
            intialAccessListItem =>
              _.isEqual(
                intialAccessListItem.value.entity,
                modifiedItem.value.entity,
              ) &&
              intialAccessListItem.value.permission !==
                modifiedItem.value.permission,
          ),
        )
        .map(augmentedOption => ({
          entity: augmentedOption.value.entity,
          permission: augmentedOption.value.permission
            ? ACLPermissionEnum[augmentedOption.value.permission]
            : ACLPermissionEnum.READ,
        })),
    ]

    await updateAccessList(requestBody)

    window.trackEvent({
      action: 'Submit',
      category:
        assetType === 'media-collection' ? 'Media collection' : 'Media library',
      label: `Share ${assetType}`,
      value: 1,
    })

    closeModal()
    dispatch([
      {
        type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
      },
      {
        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
      },
    ])
    if (getViewableParent) {
      await getViewableParent()
    }

    if (
      isRestrictedUser(user?.roles) &&
      (
        requestBody?.acl?.remove.filter(obj => obj?.entity?.id === user?.id) ||
        []
      ).length === 0
    ) {
      await refetchViewableParent()
      await refetchFolderPermission()
    }
  }

  const closeModal = (): void => {
    onClose()
    setAccessList([])
    setUserOptions([])
    setUserGroupOptions([])
    setGroupedOptions([])
    setInitialState({
      accessList: [],
      users: [],
      userGroups: [],
      mediaCollections: [],
    })
    setCreatedBy(undefined)
    setIsMediaCollectionTabSelected(false)
  }

  return (
    <Modal isOpen={isOpen} onClose={closeModal} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex alignItems="center">
            <Icon
              as={
                assetType === 'folder'
                  ? RiFolder3Line
                  : assetType === 'file'
                  ? RiFile2Line
                  : MdCollections
              }
              boxSize={6}
              color="appBattleshipGrey"
              backgroundColor="appPaleGrey"
              padding="0.1em"
              borderRadius="50%"
              marginRight="0.5em"
            />
            <Text isTruncated maxW="300px">
              {`${t('share', 'Share')} '${assetName}'`}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px="10px">
          {assetType !== 'media-collection' ? (
            <Tabs>
              <TabList marginX="1em">
                <Tooltip
                  placement="top-start"
                  label={t(
                    'only_restricted_user',
                    'Assets and media collections can only be shared with restricted users',
                  )}
                >
                  <Tab
                    onClick={() => {
                      setIsMediaCollectionTabSelected(false)
                    }}
                  >
                    {t('users_and_groups', 'Restricted users & groups')}
                  </Tab>
                </Tooltip>
                <Tab
                  onClick={() => {
                    setIsMediaCollectionTabSelected(true)
                  }}
                >
                  {t(
                    'view_media_collection_associations',
                    'View media collection associations',
                  )}
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <VStack w="100%" spacing={6}>
                    {loadingAccessList ? (
                      <Center height="100%">
                        <Spinner
                          thickness="4px"
                          speed="0.65s"
                          emptyColor="appPaleGrey"
                          color="appBlue"
                          size="xl"
                        />
                      </Center>
                    ) : (
                      <>
                        <Flex w="100%" justify="space-between">
                          <Flex w="71%">
                            <IKReactSelect
                              menuPlacement="auto"
                              variant="compact"
                              placeholder={t(
                                'select_users_groups',
                                'Select restricted users and/or groups',
                              )}
                              options={groupedOptions}
                              value={selectedOptions}
                              components={{ MultiValueLabel }}
                              onChange={options => {
                                setSelectedOptions(options)
                              }}
                              isMulti
                              isClearable
                              width="100%"
                              stylesVariant="share_modal_custom"
                              filterOption={filterOptions}
                            />
                          </Flex>
                          <Flex
                            borderWidth="1px"
                            borderRadius="5px"
                            w="27%"
                            justifyContent="space-between"
                          >
                            <PermissionMenu
                              options={Object.values(
                                PermissionDescriptionMap,
                              ).map(permissionItem =>
                                createPermissionOptions({
                                  label: permissionItem.label,
                                  value: permissionItem.value,
                                  description:
                                    assetType === 'folder'
                                      ? permissionItem.description_folder
                                      : permissionItem.description_file,
                                }),
                              )}
                              initialValue={
                                PermissionDescriptionMap[ACLPermissionEnum.READ]
                                  .label
                              }
                              onChange={permission => {
                                setSelectedPermission(permission)
                              }}
                            />
                          </Flex>
                        </Flex>

                        <Grid
                          width="100%"
                          templateColumns="repeat(1, minmax(0px, 1fr))"
                          columnGap={5}
                          rowGap={5}
                          paddingBottom={10}
                          overflow="scroll"
                          borderWidth="1px"
                          borderRadius="5px"
                          padding="8px 8px"
                          maxH="300px"
                        >
                          {createdBy ? (
                            <GridItem key={createdBy.userId}>
                              <Flex w="100%" justify="space-between">
                                <UserLabel
                                  userName={createdBy.name}
                                  userEmail={createdBy.email}
                                />
                                <Text color="#A0AEC0" paddingRight="8px">
                                  {t('owner', 'Owner')}
                                </Text>
                              </Flex>
                            </GridItem>
                          ) : null}
                          {accessList.map(accessListItem => (
                            <GridItem key={accessListItem.value.entity.id}>
                              <Flex w="100%" justify="space-between">
                                {accessListItem.label}
                                <Flex alignItems="center">
                                  {accessListItem.value.inherited ? (
                                    <InheritencePopover
                                      inheritence={
                                        accessListItem.value.inherited
                                      }
                                      onClose={closeModal}
                                    />
                                  ) : null}
                                  <PermissionMenu
                                    options={Object.values(
                                      PermissionDescriptionMap,
                                    ).map(permissionItem => {
                                      if (accessListItem.value.inherited) {
                                        const foundInitialAccessListItem =
                                          initialState.accessList.find(
                                            initialAccessListItem =>
                                              _.isEqual(
                                                initialAccessListItem.value
                                                  .entity,
                                                accessListItem.value.entity,
                                              ),
                                          )
                                        const highestInheritedPermission =
                                          getHighestInheritedPermisssion(
                                            _.map(
                                              accessListItem.value.inherited,
                                              'permission',
                                            ),
                                          )
                                        if (
                                          foundInitialAccessListItem?.value
                                            .permission &&
                                          aclPermissionOrderMapping[
                                            highestInheritedPermission
                                          ] >
                                            aclPermissionOrderMapping[
                                              permissionItem.value
                                            ]
                                        ) {
                                          return createPermissionOptions({
                                            label: permissionItem.label,
                                            value: permissionItem.value,
                                            description:
                                              assetType === 'folder'
                                                ? permissionItem.description_folder
                                                : assetType === 'file'
                                                ? permissionItem.description_file
                                                : permissionItem.description_collection,

                                            isDisabled: true,
                                          })
                                        }
                                      }
                                      return createPermissionOptions({
                                        label: permissionItem.label,
                                        value: permissionItem.value,
                                        description:
                                          assetType === 'folder'
                                            ? permissionItem.description_folder
                                            : assetType === 'file'
                                            ? permissionItem.description_file
                                            : permissionItem.description_collection,
                                      })
                                    })}
                                    initialValue={
                                      accessListItem.value.permission
                                        ? PermissionDescriptionMap[
                                            accessListItem.value.permission
                                          ].label
                                        : ''
                                    }
                                    onChange={permission => {
                                      const modifiedAclItem = accessList.find(
                                        item => _.isEqual(item, accessListItem),
                                      )
                                      if (modifiedAclItem) {
                                        modifiedAclItem.value.permission =
                                          ACLPermissionEnum[permission]
                                        const newAccessList = accessList.map(
                                          item => {
                                            if (
                                              _.isEqual(
                                                modifiedAclItem.value.entity,
                                                item.value.entity,
                                              )
                                            ) {
                                              return modifiedAclItem
                                            }
                                            return item
                                          },
                                        )
                                        setAccessList(newAccessList)
                                      }
                                    }}
                                    onRemove={
                                      accessListItem.value.inherited
                                        ? undefined
                                        : () => {
                                            const removedItem = accessList.find(
                                              item =>
                                                _.isEqual(item, accessListItem),
                                            )
                                            if (
                                              removedItem?.value.entity.type ===
                                              ACLEntityTypeEnum.USER
                                            ) {
                                              setUserOptions([
                                                ...userOptions,
                                                removedItem,
                                              ])
                                            } else if (
                                              removedItem?.value.entity.type ===
                                              ACLEntityTypeEnum.USER_GROUP
                                            ) {
                                              setUserGroupOptions([
                                                ...userGroupOptions,
                                                removedItem,
                                              ])
                                            }
                                            setAccessList(
                                              accessList.filter(
                                                item =>
                                                  !_.isEqual(item, removedItem),
                                              ),
                                            )
                                          }
                                    }
                                  />
                                </Flex>
                              </Flex>
                            </GridItem>
                          ))}
                          {!createdBy && !accessList.length ? (
                            <GridItem>
                              <Flex w="100%" justifyContent="center">
                                <Text color="appBattleshipGrey">
                                  {t(
                                    'not_shared_with_user_or_group',
                                    'Not shared with any restricted user or group',
                                  )}
                                </Text>
                              </Flex>
                            </GridItem>
                          ) : null}
                        </Grid>
                      </>
                    )}
                  </VStack>
                </TabPanel>
                <TabPanel>
                  <VStack spacing={4} alignItems="flex-start" w="full">
                    {loadingAccessList ? (
                      <Center height="100%">
                        <Spinner
                          thickness="4px"
                          speed="0.65s"
                          emptyColor="appPaleGrey"
                          color="appBlue"
                          size="xl"
                        />
                      </Center>
                    ) : (
                      <Grid
                        width="100%"
                        templateColumns="repeat(1, minmax(0px, 1fr))"
                        columnGap={5}
                        rowGap={5}
                        paddingBottom={10}
                        overflow="scroll"
                        borderWidth="1px"
                        borderRadius="5px"
                        padding="8px 8px"
                        maxH="300px"
                      >
                        {mediaCollectionList.map(accessListItem => (
                          <GridItem key={accessListItem.value.entity.id}>
                            <Flex w="100%" justify="space-between">
                              <Flex>
                                <MediaCollectionLabel
                                  name={
                                    (
                                      accessListItem.extraInfo as ShareModalMediaCollection
                                    ).name ?? ''
                                  }
                                  size={
                                    (
                                      accessListItem.extraInfo as ShareModalMediaCollection
                                    ).size ?? 0
                                  }
                                  mediaCollectionId={
                                    (
                                      accessListItem.extraInfo as ShareModalMediaCollection
                                    ).mediaCollectionId ?? ''
                                  }
                                  onClose={closeModal}
                                />
                              </Flex>
                              <HStack alignItems="center">
                                {!isRestrictedUser(user?.roles) ||
                                (
                                  accessListItem.extraInfo as ShareModalMediaCollection
                                ).permission === ACLPermissionEnum.MANAGE ? (
                                  <MediaCollectionAclPopover
                                    createdBy={
                                      (
                                        accessListItem.extraInfo as ShareModalMediaCollection
                                      ).createdBy
                                    }
                                    mediaCollectionId={
                                      accessListItem.value.entity.id
                                    }
                                    onClose={closeModal}
                                    accessList={
                                      (
                                        accessListItem.extraInfo as ShareModalMediaCollection
                                      ).aclLabels
                                    }
                                  />
                                ) : (
                                  <Tooltip
                                    label={t(
                                      'sufficient_permission_msg',
                                      'You do not have sufficient permission for this operation.',
                                    )}
                                    shouldWrapChildren
                                  >
                                    <IconButton
                                      icon={
                                        <Icon
                                          as={HiOutlineUserGroup}
                                          boxSize="1.5rem"
                                        />
                                      }
                                      aria-label="Info icon"
                                      color={colors.appDarkIndigo}
                                      isDisabled={true}
                                      variant="link"
                                    />
                                  </Tooltip>
                                )}
                                {accessListItem.value.inherited ? (
                                  <InheritencePopover
                                    inheritence={accessListItem.value.inherited}
                                    onClose={closeModal}
                                  />
                                ) : null}
                              </HStack>
                            </Flex>
                          </GridItem>
                        ))}
                        {!mediaCollectionList.length ? (
                          <GridItem>
                            <Flex w="100%" justifyContent="center">
                              <Text color="appBattleshipGrey">
                                {t(
                                  'nothing_media_collection_association',
                                  'Not added to any media collection',
                                )}
                              </Text>
                            </Flex>
                          </GridItem>
                        ) : null}
                      </Grid>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <VStack w="100%" spacing={6} p={6}>
              {loadingAccessList ? (
                <Center height="100%">
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="appPaleGrey"
                    color="appBlue"
                    size="xl"
                  />
                </Center>
              ) : (
                <>
                  <Flex w="100%" justify="space-between">
                    <Flex w="71%">
                      <IKReactSelect
                        menuPlacement="auto"
                        variant="compact"
                        placeholder={t(
                          'select_users_groups',
                          'Select restricted users and/or groups',
                        )}
                        options={groupedOptions}
                        value={selectedOptions}
                        components={{ MultiValueLabel }}
                        onChange={options => {
                          setSelectedOptions(options)
                        }}
                        isMulti
                        isClearable
                        width="100%"
                        stylesVariant="share_modal_custom"
                        filterOption={filterOptions}
                      />
                    </Flex>
                    <Flex
                      borderWidth="1px"
                      borderRadius="5px"
                      w="27%"
                      justifyContent="right"
                    >
                      <PermissionMenu
                        options={Object.values(PermissionDescriptionMap).map(
                          permissionItem =>
                            createPermissionOptions({
                              label: permissionItem.label,
                              value: permissionItem.value,
                              description:
                                permissionItem.description_collection,
                            }),
                        )}
                        initialValue={
                          PermissionDescriptionMap[ACLPermissionEnum.READ].label
                        }
                        onChange={permission => {
                          setSelectedPermission(permission)
                        }}
                      />
                    </Flex>
                  </Flex>

                  <Grid
                    width="100%"
                    templateColumns="repeat(1, minmax(0px, 1fr))"
                    columnGap={5}
                    rowGap={5}
                    paddingBottom={10}
                    overflow="scroll"
                    borderWidth="1px"
                    borderRadius="5px"
                    padding="8px 8px"
                    maxH="300px"
                  >
                    {createdBy ? (
                      <GridItem key={createdBy.userId}>
                        <Flex w="100%" justify="space-between">
                          <UserLabel
                            userName={createdBy.name}
                            userEmail={createdBy.email}
                          />
                          <Text color="#A0AEC0" paddingRight="8px">
                            {t('owner', 'Owner')}
                          </Text>
                        </Flex>
                      </GridItem>
                    ) : null}
                    {accessList.map(accessListItem => (
                      <GridItem key={accessListItem.value.entity.id}>
                        <Flex w="100%" justify="space-between">
                          {accessListItem.label}
                          <Flex alignItems="center">
                            {accessListItem.value.inherited ? (
                              <InheritencePopover
                                inheritence={accessListItem.value.inherited}
                                onClose={closeModal}
                              />
                            ) : null}
                            <PermissionMenu
                              options={Object.values(
                                PermissionDescriptionMap,
                              ).map(permissionItem => {
                                if (accessListItem.value.inherited) {
                                  const foundInitialAccessListItem =
                                    initialState.accessList.find(
                                      initialAccessListItem =>
                                        _.isEqual(
                                          initialAccessListItem.value.entity,
                                          accessListItem.value.entity,
                                        ),
                                    )
                                  const highestInheritedPermission =
                                    getHighestInheritedPermisssion(
                                      _.map(
                                        accessListItem.value.inherited,
                                        'permission',
                                      ),
                                    )
                                  if (
                                    foundInitialAccessListItem?.value
                                      .permission &&
                                    aclPermissionOrderMapping[
                                      highestInheritedPermission
                                    ] >
                                      aclPermissionOrderMapping[
                                        permissionItem.value
                                      ]
                                  ) {
                                    return createPermissionOptions({
                                      label: permissionItem.label,
                                      value: permissionItem.value,
                                      description:
                                        permissionItem.description_collection,

                                      isDisabled: true,
                                    })
                                  }
                                }
                                return createPermissionOptions({
                                  label: permissionItem.label,
                                  value: permissionItem.value,
                                  description:
                                    permissionItem.description_collection,
                                })
                              })}
                              initialValue={
                                accessListItem.value.permission
                                  ? PermissionDescriptionMap[
                                      accessListItem.value.permission
                                    ].label
                                  : ''
                              }
                              onChange={permission => {
                                const modifiedAclItem = accessList.find(item =>
                                  _.isEqual(item, accessListItem),
                                )
                                if (modifiedAclItem) {
                                  modifiedAclItem.value.permission =
                                    ACLPermissionEnum[permission]
                                  const newAccessList = accessList.map(item => {
                                    if (
                                      _.isEqual(
                                        modifiedAclItem.value.entity,
                                        item.value.entity,
                                      )
                                    ) {
                                      return modifiedAclItem
                                    }
                                    return item
                                  })
                                  setAccessList(newAccessList)
                                }
                              }}
                              onRemove={
                                accessListItem.value.inherited
                                  ? undefined
                                  : () => {
                                      const removedItem = accessList.find(
                                        item => _.isEqual(item, accessListItem),
                                      )
                                      if (
                                        removedItem?.value.entity.type ===
                                        ACLEntityTypeEnum.USER
                                      ) {
                                        setUserOptions([
                                          ...userOptions,
                                          removedItem,
                                        ])
                                      } else if (
                                        removedItem?.value.entity.type ===
                                        ACLEntityTypeEnum.USER_GROUP
                                      ) {
                                        setUserGroupOptions([
                                          ...userGroupOptions,
                                          removedItem,
                                        ])
                                      }
                                      setAccessList(
                                        accessList.filter(
                                          item => !_.isEqual(item, removedItem),
                                        ),
                                      )
                                    }
                              }
                            />
                          </Flex>
                        </Flex>
                      </GridItem>
                    ))}
                    {!createdBy && !accessList.length ? (
                      <GridItem>
                        <Flex w="100%" justifyContent="center">
                          <Text color="appBattleshipGrey">
                            {t(
                              'not_shared_with_user_or_group',
                              'Not shared with any restricted user or group',
                            )}
                          </Text>
                        </Flex>
                      </GridItem>
                    ) : null}
                  </Grid>
                </>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter
          mt="20px"
          borderTop="1px solid #e7eaf0"
          style={{ gap: '1em' }}
        >
          <Button onClick={closeModal} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            display={isMediaCollectionTabSelected ? 'none' : 'block'}
            onClick={onSubmit}
            isLoading={loadingAccessList}
            isDisabled={
              _.isEqual(initialState.accessList, accessList) &&
              selectedOptions.length === 0
            }
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const InheritencePopover = ({
  inheritence,
  onClose,
}: {
  inheritence: InheritenceListItem[]
  onClose(): void
}): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Popover trigger="click" placement="bottom-end">
      <PopoverTrigger>
        <Flex>
          <Tooltip
            label={t('access_control_summary', 'access control summary')}
          >
            <Icon
              as={InheritedPermissionIcon}
              color={colors.appBlueyGrey}
              marginRight={2}
              h={6}
              w={6}
            />
          </Tooltip>
        </Flex>
      </PopoverTrigger>
      <PopoverContent maxWidth="unset" width="unset">
        <PopoverArrow />
        <PopoverBody>
          <VStack spacing={4} alignItems="flex-start">
            {(inheritence ?? []).map(inheritenceItem => (
              <Flex direction="column" key={inheritenceItem.folderPath}>
                <Text>
                  {inheritenceItem.permission
                    ? `${
                        PermissionDescriptionMap[inheritenceItem.permission]
                          .label
                      }${' '}`
                    : 'Shared '}
                  <chakra.span fontSize="12px" color="appBattleshipGrey">
                    via
                  </chakra.span>{' '}
                  <chakra.span
                    as={Link}
                    to={`/media-library/${Base64.encode(
                      inheritenceItem.folderPath,
                    )}`}
                    onClick={onClose}
                    fontWeight="semibold"
                    color="appBattleshipGrey"
                    _hover={{
                      color: colors.appClearBlue,
                    }}
                  >
                    <Tooltip label={inheritenceItem.folderPath}>
                      {inheritenceItem.name}
                    </Tooltip>
                  </chakra.span>
                </Text>
              </Flex>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
interface PermissionMenuProps {
  initialValue: string
  options: Array<{
    label: JSX.Element
    value: string
    isDisabled?: boolean
  }>
  onChange?(selectedValue: string): void
  onRemove?(): void
}

const PermissionMenu = ({
  initialValue,
  options,
  onChange,
  onRemove,
}: PermissionMenuProps): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<string>(initialValue)
  const { t } = useTranslation()

  return (
    <Menu>
      <MenuButton w="100%">
        <Flex
          w="100%"
          justifyContent="space-between"
          alignItems="center"
          paddingX={2}
        >
          {selectedOption}
          <ChevronDownIcon />
        </Flex>
      </MenuButton>
      <MenuList minW="250px" maxH="350px" overflow="scroll">
        {options.map(option => (
          <MenuItem
            isDisabled={option.isDisabled}
            key={`permission-menu-${option.value}`}
            value={option.value}
            onClick={e => {
              setSelectedOption(PermissionDescriptionMap[option.value].label)
              onChange?.(option.value)
            }}
          >
            {option.label}
          </MenuItem>
        ))}
        {onRemove && (
          <>
            <MenuDivider />
            <MenuItem
              onClick={() => {
                setSelectedOption('')
                onRemove()
              }}
              color="appBrick"
            >
              {t('remove_access', 'Remove access')}
            </MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  )
}

export const UserLabel = ({
  userName,
  userEmail,
}: {
  userName: string
  userEmail: string
}): JSX.Element => (
  <Flex alignItems="center" maxW="250px">
    <Avatar size="sm" name={userName} />
    <Tooltip
      label={
        <Flex direction="column">
          <Text>{`Name: ${userName}`}</Text>
          <Text>{`Email: ${userEmail}`}</Text>
        </Flex>
      }
    >
      <Flex direction="column" w="85%">
        <Text as="span" isTruncated marginX="0.75em">
          {userName}
        </Text>
        <Text as="span" isTruncated marginX="0.75em" fontSize="12px">
          {userEmail}
        </Text>
      </Flex>
    </Tooltip>
  </Flex>
)

export const UserGroupLabel = ({
  groupLabel,
  members,
}: {
  groupLabel: string
  members: number
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Flex alignItems="center" w="250px">
      <Flex
        borderRadius="full"
        backgroundColor={colors.appGreyTwo}
        w={8}
        h={8}
        alignItems="center"
        justifyContent="center"
      >
        <Icon
          as={HiUserGroup}
          backgroundColor={colors.appGreyTwo}
          borderRadius="full"
        />
      </Flex>

      <Flex direction="column" w="85%">
        <Tooltip label={groupLabel}>
          <Text as="span" isTruncated marginX="0.75em">
            {groupLabel}
          </Text>
        </Tooltip>
        <Text as="span" isTruncated marginX="0.75em" fontSize="12px">
          {`${members} ${t('members', 'members')}`}
        </Text>
      </Flex>
    </Flex>
  )
}

export const MediaCollectionLabel = ({
  name,
  size,
  mediaCollectionId,
  onClose,
}: {
  name: string
  size: number
  mediaCollectionId: string
  onClose(): void
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Flex alignItems="center" w="250px">
      <Flex
        borderRadius="full"
        backgroundColor={colors.appGreyTwo}
        w={8}
        h={8}
        alignItems="center"
        justifyContent="center"
      >
        <Icon
          as={MdCollections}
          backgroundColor={colors.appGreyTwo}
          borderRadius="full"
        />
      </Flex>
      <Flex direction="column" w="85%">
        <Flex>
          <Tooltip label={name}>
            <Text
              as={Link}
              to={`/media-collections/${mediaCollectionId}`}
              onClick={onClose}
              _hover={{
                color: colors.appClearBlue,
              }}
            >
              <Text as="span" isTruncated marginX="0.75em">
                {name}
              </Text>
            </Text>
          </Tooltip>
        </Flex>
        <Text as="span" isTruncated marginX="0.75em" fontSize="12px">
          {`${size} ${t('items', 'items')}`}
        </Text>
      </Flex>
    </Flex>
  )
}

export const MediaCollectionAclPopover = ({
  createdBy,
  accessList,
  mediaCollectionId,
  onClose,
}: {
  createdBy: CreatedBy | undefined
  accessList: any
  mediaCollectionId: string
  onClose(): void
}): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Popover trigger="click" placement="bottom-end">
      <PopoverTrigger>
        <Flex>
          <Tooltip label={t('shared_with', 'Shared with')}>
            <IconButton
              icon={<Icon as={HiOutlineUserGroup} boxSize="1.5rem" />}
              aria-label="Info icon"
              color={colors.appDarkIndigo}
              variant="link"
            />
          </Tooltip>
        </Flex>
      </PopoverTrigger>
      <PopoverContent maxWidth="unset" width="unset">
        <PopoverArrow />
        <PopoverBody padding={4}>
          <Flex direction="column">
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Text fontWeight="semibold">
                {t('shared_with', 'Shared with')}
              </Text>
              <ChakraLink
                as={Link}
                to={`/media-collections/${mediaCollectionId}`}
                onClick={onClose}
                ml={8}
              >
                {t(
                  'go_to_media_collection_link_copy',
                  'Go to media collection',
                )}
              </ChakraLink>
            </Flex>
            <Grid
              width="100%"
              templateColumns="repeat(1, minmax(0px, 1fr))"
              columnGap={5}
              rowGap={5}
              paddingBottom={10}
              overflow="scroll"
              borderWidth="1px"
              borderRadius="5px"
              padding="8px 8px"
              maxH="250px"
            >
              {createdBy ? (
                <GridItem key={createdBy.userId}>
                  <Flex w="100%" justify="space-between">
                    <UserLabel
                      userName={createdBy.name}
                      userEmail={createdBy.email}
                    />
                    <Text color="#A0AEC0" paddingRight="8px">
                      {t('owner', 'Owner')}
                    </Text>
                  </Flex>
                </GridItem>
              ) : null}
              {accessList && accessList.length
                ? accessList.map(accessListItem => (
                    <GridItem key={accessListItem.value.entity.id}>
                      <Flex justify="space-between" w="26rem">
                        {accessListItem.label}
                        <Flex alignItems="center">
                          <Text color="appBattleshipGrey">
                            {
                              PermissionDescriptionMap[
                                accessListItem.value.permission
                              ].label
                            }
                          </Text>
                        </Flex>
                      </Flex>
                    </GridItem>
                  ))
                : null}
              {!createdBy && !accessList.length ? (
                <GridItem>
                  <Flex w="100%" justifyContent="center">
                    <Text color="appBattleshipGrey">
                      {t(
                        'not_shared_with_user_or_group',
                        'Not shared with any restricted user or group',
                      )}
                    </Text>
                  </Flex>
                </GridItem>
              ) : null}
            </Grid>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default ShareModal
